import { useState, useMemo } from 'react'
import { Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { Button } from '@/components/ui/Button'
import {
  FIXED_SLIDE_LAYOUT_BLOCKS,
  buildCategoryLayoutBlocks,
  formatLayoutPercent,
  formatLayoutTemplate,
  getFixedBlockValue,
  getZoneLabel,
  type FixedLayoutBlockDef,
} from './layoutSlideBlocks'

interface LayoutSlidersProps {
  layout: LayoutConfig
  readonly: boolean
  categoryNames?: string[]
  onSliderChange: (key: keyof LayoutConfig, value: number) => void
  onCategoryCoordChange?: (category: string, axis: 'x' | 'y', value: number) => void
}

function BlockNumberBadge({ n }: { n: number }) {
  return (
    <span
      className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-md bg-accent text-white text-[11px] font-black shrink-0"
      title={`Bloco ${n}`}
    >
      {n}
    </span>
  )
}

function PercentSlider({
  label,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  disabled: boolean
  onChange: (v: number) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold text-text-label uppercase tracking-wider">{label}</span>
        <span className="text-[10px] text-accent font-mono font-bold">{formatLayoutPercent(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className="w-full h-1.5 bg-[var(--glass-border)] rounded-full appearance-none cursor-pointer accent-[var(--brand-primary)] disabled:opacity-50"
      />
    </div>
  )
}

function FixedBlockControl({
  block,
  layout,
  readonly,
  onSliderChange,
}: {
  block: FixedLayoutBlockDef
  layout: LayoutConfig
  readonly: boolean
  onSliderChange: (key: keyof LayoutConfig, value: number) => void
}) {
  if (!block.layoutKey) return null
  const value = getFixedBlockValue(layout, block)
  const axisLabel = block.axis === 'width' ? 'Largura' : block.axis === 'y' ? 'Pos Y' : 'Pos X'

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-lg p-3 space-y-2 shadow-sm">
      <div className="flex items-start gap-2">
        <BlockNumberBadge n={block.number} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-text-primary leading-tight">{block.label}</p>
          <p className="text-[9px] text-text-muted mt-0.5">{block.hint}</p>
        </div>
      </div>
      <PercentSlider
        label={axisLabel}
        value={value}
        min={block.min}
        max={block.max}
        step={block.step}
        disabled={readonly}
        onChange={(v) => onSliderChange(block.layoutKey!, v)}
      />
      {block.axis === 'width' && (
        <p className="text-[9px] text-text-muted">
          X dos textos verdes ≈ {formatLayoutPercent(value * 0.06)} (automático)
        </p>
      )}
    </div>
  )
}

export function LayoutSliders({
  layout,
  readonly,
  categoryNames = [],
  onSliderChange,
  onCategoryCoordChange,
}: LayoutSlidersProps) {
  const [copied, setCopied] = useState(false)

  const categoryBlocks = useMemo(
    () => buildCategoryLayoutBlocks(layout, categoryNames),
    [layout, categoryNames],
  )

  const blocksByZone = useMemo(() => {
    const map = new Map<string, FixedLayoutBlockDef[]>()
    for (const block of FIXED_SLIDE_LAYOUT_BLOCKS) {
      const list = map.get(block.zone) || []
      list.push(block)
      map.set(block.zone, list)
    }
    return map
  }, [])

  const handleCopyTemplate = async () => {
    const text = formatLayoutTemplate(layout, categoryNames)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      toast.success('Template de posições copiado — cole no chat para pedir ajustes por número de bloco')
      setTimeout(() => setCopied(false), 2500)
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.')
    }
  }

  const handleCategoryChange = (category: string, axis: 'x' | 'y', value: number) => {
    onCategoryCoordChange?.(category, axis, value)
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-bold text-accent uppercase tracking-wider">
            Controles de Layout de Elementos
          </h3>
          <p className="text-[10px] text-text-muted mt-1 max-w-xl">
            Cada bloco tem um número no preview e aqui. Copie o template e envie, por exemplo:{' '}
            <span className="text-text-primary font-mono">Bloco 6 Y 78%, Bloco 13 X 38% Y 12%</span>
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0 text-[10px] gap-1.5"
          onClick={handleCopyTemplate}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copiado' : 'Copiar template de posições'}
        </Button>
      </div>

      {Array.from(blocksByZone.entries()).map(([zone, blocks]) => (
        <div key={zone}>
          <h4 className="text-[10px] font-bold text-text-label uppercase tracking-wider mb-2 border-b border-[var(--glass-border)] pb-1">
            {getZoneLabel(zone as FixedLayoutBlockDef['zone'])}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {blocks.map((block) => (
              <FixedBlockControl
                key={block.id}
                block={block}
                layout={layout}
                readonly={readonly}
                onSliderChange={onSliderChange}
              />
            ))}
          </div>
        </div>
      ))}

      {categoryBlocks.length > 0 && (
        <div>
          <h4 className="text-[10px] font-bold text-text-label uppercase tracking-wider mb-2 border-b border-[var(--glass-border)] pb-1">
            Categorias de entregáveis (posição individual)
          </h4>
          <p className="text-[9px] text-text-muted mb-2">
            Se X/Y não forem definidos, usam a grade dos blocos 10–12. Arraste no preview ou ajuste abaixo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categoryBlocks.map((cat) => (
              <div
                key={cat.category}
                className="bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-lg p-3 space-y-2 shadow-sm"
              >
                <div className="flex items-start gap-2">
                  <BlockNumberBadge n={cat.number} />
                  <p className="text-[11px] font-bold text-text-primary leading-tight truncate">
                    {cat.category}
                  </p>
                </div>
                <PercentSlider
                  label="Pos X"
                  value={cat.x}
                  min={Math.max(25, (layout.leftBlockWidth || 35) + 2)}
                  max={95}
                  step={0.5}
                  disabled={readonly || !onCategoryCoordChange}
                  onChange={(v) => handleCategoryChange(cat.category, 'x', v)}
                />
                <PercentSlider
                  label="Pos Y"
                  value={cat.y}
                  min={5}
                  max={90}
                  step={0.5}
                  disabled={readonly || !onCategoryCoordChange}
                  onChange={(v) => handleCategoryChange(cat.category, 'y', v)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
